/**
 * Membership SDK - Data fetching and utilities for membership service
 * 
 * Provides typed access to membership service APIs from any plugin.
 * 
 * Usage:
 *   const identity = await sdk.membership.getIdentity('auth:account:empowernow:test');
 *   const agents = await sdk.membership.getAgents({ status: 'active' });
 */

// ============================================================================
// Types
// ============================================================================

export interface Identity {
  id: string;
  name?: string;
  display_name?: string;
  email?: string;
  description?: string;
  identity_type: 'Person' | 'AIAgent' | 'Group' | 'ServiceAccount';
  status: 'active' | 'inactive' | 'orphaned' | 'pending';
  system?: string;
  system_identifier?: string;
  delegation_count?: number;
  capabilities_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Agent extends Identity {
  identity_type: 'AIAgent';
  mcp_service_name?: string;
  controlled_by?: string;
  owner_arn?: string;
  created_by?: string;
}

export interface Person extends Identity {
  identity_type: 'Person';
  given_name?: string;
  family_name?: string;
  job_title?: string;
  department?: string;
}

export interface Group extends Identity {
  identity_type: 'Group';
  member_count?: number;
  group_type?: 'security' | 'distribution' | 'role';
}

export interface Delegation {
  delegation_id: string;
  id?: string;  // Alias for delegation_id
  delegator_id: string;
  user_id?: string;  // Canonical name (v2.3)
  delegatee_id: string;
  agent_id?: string;  // Canonical name (v2.3)
  delegator_name?: string;
  delegatee_name?: string;
  status: 'active' | 'expired' | 'revoked' | 'paused' | 'suspended' | 'pending';
  trust_level?: 'basic' | 'elevated' | 'full';
  capability_ids?: string[];
  constraints?: Record<string, unknown>;
  created_at: string;
  expires_at?: string;
  version_token?: string;
}

/**
 * Request to create a new delegation
 */
export interface CreateDelegationRequest {
  delegate_arn: string;  // Agent ARN
  systems?: string[];  // External systems
  capability_ids?: string[];  // Fine-grained capabilities
  trust_level?: 'basic' | 'elevated' | 'full';
  constraints?: {
    spend_cap?: number;
    max_actions?: number;
    time_window?: string;
  };
  expires_at?: string;
  requires_approval?: boolean;
}

/**
 * Request to extend a delegation
 */
export interface ExtendDelegationRequest {
  days?: number;
  expires_at?: string;
}

/**
 * Capability definition from registry
 */
export interface CapabilityDefinition {
  id: string;
  system: string;
  display_name: string;
  description?: string;
  risk_level: 'low' | 'medium' | 'high';
  required_trust_level: 'basic' | 'elevated' | 'full';
}

export interface Capability {
  capability_id: string;
  name: string;
  tool_id?: string;
  granted_at: string;
}

export interface IdentityQuery {
  identity_type?: string;
  status?: string;
  search?: string;
  limit?: number;
  skip?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  skip: number;
}

// ============================================================================
// Resource Instance Types (v2.4 ReBAC)
// ============================================================================

/**
 * A concrete resource instance in ReBAC mode
 */
export interface ResourceInstance {
  id: string;
  resource_type: string;
  application_id: string;
  name: string;
  description?: string;
  mode: 'abac' | 'rebac';
  attributes: Record<string, unknown>;
  owner_id?: string;
  parent_id?: string;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

/**
 * Request to create a resource instance
 */
export interface CreateResourceInstanceRequest {
  id?: string;
  resource_type: string;
  application_id: string;
  name: string;
  description?: string;
  attributes?: Record<string, unknown>;
  owner_id?: string;
  parent_id?: string;
}

/**
 * Request to update a resource instance
 */
export interface UpdateResourceInstanceRequest {
  name?: string;
  description?: string;
  attributes?: Record<string, unknown>;
  status?: 'active' | 'archived';
  parent_id?: string;
}

/**
 * Owner of a resource instance
 */
export interface InstanceOwner {
  id: string;
  display_name?: string;
  email?: string;
  ownership: {
    created_at: string;
    created_by: string;
    is_primary: boolean;
    reason?: string;
  };
}

/**
 * Instance delegation for fine-grained resource access
 */
export interface InstanceDelegation {
  id: string;
  delegator_id: string;
  delegate_id: string;
  resource_instance_id: string;
  allowed_actions: string[];
  status: 'active' | 'paused' | 'revoked' | 'expired';
  constraints?: Record<string, unknown>;
  valid_from?: string;
  expires_at?: string;
  created_at: string;
  created_by: string;
}

/**
 * Request to create an instance delegation
 */
export interface CreateInstanceDelegationRequest {
  delegate_id: string;
  allowed_actions: string[];
  constraints?: Record<string, unknown>;
  valid_from?: string;
  expires_at?: string;
}

// ============================================================================
// Feature Bundles & Permission Sets (v2.4)
// ============================================================================

/**
 * Feature bundle - preconfigured set of permissions for a role/tier
 */
export interface FeatureBundle {
  id: string;
  name: string;
  description?: string;
  application_id: string;
  tier: 'starter' | 'professional' | 'enterprise' | 'custom';
  parent_bundle_id?: string;
  permissions: string[];
  metadata?: Record<string, unknown>;
  status: 'active' | 'deprecated';
  created_at: string;
  updated_at?: string;
}

/**
 * Resolved bundle with inherited permissions expanded
 */
export interface ResolvedBundle extends FeatureBundle {
  inherited_permissions: string[];
  all_permissions: string[];
  inheritance_chain: string[];
}

/**
 * Bundle assignment for a user
 */
export interface BundleAssignment {
  id: string;
  user_id: string;
  bundle_id: string;
  bundle_name: string;
  assigned_at: string;
  assigned_by: string;
  effective_permissions: string[];
}

/**
 * Diff between two bundles
 */
export interface BundleDiff {
  from_bundle_id: string;
  to_bundle_id: string;
  added_permissions: string[];
  removed_permissions: string[];
  unchanged_permissions: string[];
  requires_approval: boolean;
  risk_level: 'low' | 'medium' | 'high';
}

/**
 * Permission set - additive permissions on top of bundles
 */
export interface PermissionSet {
  id: string;
  name: string;
  description?: string;
  application_id: string;
  permissions: string[];
  requires_approval: boolean;
  approval_workflow_id?: string;
  metadata?: Record<string, unknown>;
  status: 'active' | 'deprecated';
  created_at: string;
}

/**
 * Effective permissions for a user (bundle + permission sets)
 */
export interface EffectivePermissions {
  user_id: string;
  bundle_id?: string;
  bundle_permissions: string[];
  permission_set_ids: string[];
  permission_set_permissions: string[];
  all_permissions: string[];
  computed_at: string;
}

// ============================================================================
// SDK Factory
// ============================================================================

/**
 * Creates the membership SDK namespace.
 * Called internally by createPluginSdk.
 */
export function createMembershipSdk(api: {
  fetch: (path: string, init?: RequestInit) => Promise<Response>;
  get: <T = any>(path: string) => Promise<T>;
  post: <T = any>(path: string, body?: unknown) => Promise<T>;
  patch: <T = any>(path: string, body?: unknown) => Promise<T>;
  delete: <T = any>(path: string) => Promise<T>;
}) {
  
  /**
   * Fetch helper that parses JSON responses
   */
  const fetchJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
    const method = (init?.method || 'GET').toUpperCase();
    if (method === 'GET' && (!init || Object.keys(init).length === 0)) {
      return api.get<T>(path);
    }
    if (method === 'GET' && !init?.body && !init?.signal && !init?.cache) {
      // Most membership SDK GET calls are plain GETs; route them through api.get()
      // so we get SWR + conditional validators (ETag / Last-Modified).
      return api.get<T>(path);
    }
    const res = await api.fetch(path, init);
    return res.json();
  };

  return {
    // ========================================================================
    // Identity Operations
    // ========================================================================
    
    /**
     * Get a single identity by ID or ARN
     */
    getIdentity: async (id: string): Promise<Identity> => {
      return fetchJson<Identity>(`/membership/identities/${encodeURIComponent(id)}`);
    },

    /**
     * List identities with filtering and pagination
     */
    getIdentities: async (query?: IdentityQuery): Promise<PaginatedResponse<Identity>> => {
      const params = new URLSearchParams();
      if (query?.identity_type) params.set('identity_type', query.identity_type);
      if (query?.status) params.set('status', query.status);
      if (query?.search) params.set('search', query.search);
      if (query?.limit) params.set('limit', String(query.limit));
      if (query?.skip) params.set('skip', String(query.skip));
      
      return fetchJson<PaginatedResponse<Identity>>(`/membership/identities?${params}`);
    },

    /**
     * Check if an identity exists (pre-flight validation)
     */
    identityExists: async (id: string): Promise<boolean> => {
      try {
        const result = await fetchJson<{ exists: boolean }>(`/membership/identities/${encodeURIComponent(id)}/exists`);
        return result.exists;
      } catch {
        return false;
      }
    },

    // ========================================================================
    // Agent Operations
    // ========================================================================

    /**
     * Get all AI agents
     */
    getAgents: async (query?: IdentityQuery): Promise<PaginatedResponse<Agent>> => {
      const params = new URLSearchParams();
      params.set('identity_type', 'AIAgent');
      if (query?.status) params.set('status', query.status);
      if (query?.search) params.set('search', query.search);
      if (query?.limit) params.set('limit', String(query.limit));
      if (query?.skip) params.set('skip', String(query.skip));
      
      return fetchJson<PaginatedResponse<Agent>>(`/membership/identities?${params}`);
    },

    /**
     * Get agents delegated to current user
     */
    getMyAgents: async (query?: IdentityQuery): Promise<{ agents: Agent[]; total: number }> => {
      const params = new URLSearchParams();
      if (query?.status) params.set('status', query.status);
      if (query?.search) params.set('search', query.search);
      if (query?.limit) params.set('limit', String(query.limit));
      if (query?.skip) params.set('skip', String(query.skip));
      
      return fetchJson<{ agents: Agent[]; total: number }>(`/membership/self/agents?${params}`);
    },

    // ========================================================================
    // Person Operations
    // ========================================================================

    /**
     * Get all persons
     */
    getPersons: async (query?: IdentityQuery): Promise<PaginatedResponse<Person>> => {
      const params = new URLSearchParams();
      params.set('identity_type', 'Person');
      if (query?.status) params.set('status', query.status);
      if (query?.search) params.set('search', query.search);
      if (query?.limit) params.set('limit', String(query.limit));
      if (query?.skip) params.set('skip', String(query.skip));
      
      return fetchJson<PaginatedResponse<Person>>(`/membership/identities?${params}`);
    },

    // ========================================================================
    // Group Operations
    // ========================================================================

    /**
     * Get all groups
     */
    getGroups: async (query?: IdentityQuery): Promise<PaginatedResponse<Group>> => {
      const params = new URLSearchParams();
      params.set('identity_type', 'Group');
      if (query?.status) params.set('status', query.status);
      if (query?.search) params.set('search', query.search);
      if (query?.limit) params.set('limit', String(query.limit));
      if (query?.skip) params.set('skip', String(query.skip));
      
      return fetchJson<PaginatedResponse<Group>>(`/membership/identities?${params}`);
    },

    // ========================================================================
    // Delegation Operations
    // ========================================================================

    /**
     * Get delegations for an identity
     */
    getDelegations: async (identityId: string): Promise<Delegation[]> => {
      return fetchJson<Delegation[]>(`/membership/identities/${encodeURIComponent(identityId)}/delegations`);
    },

    /**
     * Get my delegations (current user)
     */
    getMyDelegations: async (): Promise<{ delegations: Delegation[]; total: number }> => {
      return fetchJson<{ delegations: Delegation[]; total: number }>(`/membership/delegations`);
    },

    /**
     * Get a single delegation by ID
     */
    getDelegation: async (delegationId: string): Promise<Delegation> => {
      return fetchJson<Delegation>(`/membership/delegations/${encodeURIComponent(delegationId)}`);
    },

    /**
     * Create a new delegation
     */
    createDelegation: async (request: CreateDelegationRequest): Promise<Delegation> => {
      return fetchJson<Delegation>(`/membership/delegations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
    },

    /**
     * Pause a delegation (user-initiated, can be resumed)
     */
    pauseDelegation: async (delegationId: string, reason?: string): Promise<Delegation> => {
      return fetchJson<Delegation>(`/membership/delegations/${encodeURIComponent(delegationId)}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
    },

    /**
     * Resume a paused/suspended delegation
     */
    resumeDelegation: async (delegationId: string): Promise<Delegation> => {
      return fetchJson<Delegation>(`/membership/delegations/${encodeURIComponent(delegationId)}/resume`, {
        method: 'POST',
      });
    },

    /**
     * Extend a delegation's expiration
     */
    extendDelegation: async (delegationId: string, request: ExtendDelegationRequest): Promise<Delegation> => {
      return fetchJson<Delegation>(`/membership/delegations/${encodeURIComponent(delegationId)}/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
    },

    /**
     * Revoke a delegation (terminal state)
     */
    revokeDelegation: async (delegationId: string, reason?: string): Promise<void> => {
      await api.fetch(`/membership/delegations/${encodeURIComponent(delegationId)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
    },

    /**
     * Update delegation capabilities
     */
    updateDelegationCapabilities: async (delegationId: string, capability_ids: string[]): Promise<Delegation> => {
      return fetchJson<Delegation>(`/membership/delegations/${encodeURIComponent(delegationId)}/capabilities`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capability_ids }),
      });
    },

    /**
     * Get available capabilities for a system
     */
    getAvailableCapabilities: async (systemKey: string): Promise<CapabilityDefinition[]> => {
      return fetchJson<CapabilityDefinition[]>(`/membership/capabilities?system=${encodeURIComponent(systemKey)}`);
    },

    /**
     * Mint a delegation token (for agent use)
     */
    mintDelegationToken: async (delegationId: string): Promise<{ token: string; expires_at: string }> => {
      return fetchJson<{ token: string; expires_at: string }>(`/membership/delegations/${encodeURIComponent(delegationId)}/token`, {
        method: 'POST',
      });
    },

    /**
     * Get capabilities for an agent
     */
    getCapabilities: async (agentId: string): Promise<Capability[]> => {
      return fetchJson<Capability[]>(`/membership/agents/${encodeURIComponent(agentId)}/capabilities`);
    },

    // ========================================================================
    // Resource Instance Operations (v2.4 ReBAC)
    // ========================================================================

    /**
     * List resource instances for an application
     */
    listResourceInstances: async (appId: string, params?: {
      resource_type?: string;
      owner_id?: string;
      status?: string;
      limit?: number;
      skip?: number;
    }): Promise<{ items: ResourceInstance[]; total: number }> => {
      const searchParams = new URLSearchParams({ application_id: appId });
      if (params?.resource_type) searchParams.set('resource_type', params.resource_type);
      if (params?.owner_id) searchParams.set('owner_id', params.owner_id);
      if (params?.status) searchParams.set('status', params.status);
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.skip) searchParams.set('skip', String(params.skip));
      return fetchJson<{ items: ResourceInstance[]; total: number }>(
        `/membership/resource-instances?${searchParams.toString()}`
      );
    },

    /**
     * Get a resource instance by ID
     */
    getResourceInstance: async (instanceId: string): Promise<ResourceInstance> => {
      return fetchJson<ResourceInstance>(`/membership/resource-instances/${encodeURIComponent(instanceId)}`);
    },

    /**
     * Create a new resource instance
     */
    createResourceInstance: async (request: CreateResourceInstanceRequest): Promise<ResourceInstance> => {
      const response = await fetchJson<{ success: boolean; instance: ResourceInstance }>(
        '/membership/resource-instances',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }
      );
      return response.instance;
    },

    /**
     * Update a resource instance
     */
    updateResourceInstance: async (instanceId: string, updates: UpdateResourceInstanceRequest): Promise<ResourceInstance> => {
      const response = await fetchJson<{ success: boolean; instance: ResourceInstance }>(
        `/membership/resource-instances/${encodeURIComponent(instanceId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updates),
        }
      );
      return response.instance;
    },

    /**
     * Delete a resource instance
     */
    deleteResourceInstance: async (instanceId: string): Promise<void> => {
      await api.fetch(`/membership/resource-instances/${encodeURIComponent(instanceId)}`, {
        method: 'DELETE',
      });
    },

    /**
     * Set/transfer ownership of a resource instance
     */
    setInstanceOwner: async (instanceId: string, ownerId: string, reason?: string): Promise<ResourceInstance> => {
      const response = await fetchJson<{ success: boolean; instance: ResourceInstance }>(
        `/membership/resource-instances/${encodeURIComponent(instanceId)}/owner`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ owner_id: ownerId, reason }),
        }
      );
      return response.instance;
    },

    /**
     * Get owners of a resource instance
     */
    getInstanceOwners: async (instanceId: string, includeDeputies?: boolean): Promise<InstanceOwner[]> => {
      const params = includeDeputies ? '?include_deputies=true' : '';
      return fetchJson<InstanceOwner[]>(
        `/membership/resource-instances/${encodeURIComponent(instanceId)}/owners${params}`
      );
    },

    /**
     * Delegate actions on a resource instance
     */
    delegateInstance: async (instanceId: string, request: CreateInstanceDelegationRequest): Promise<InstanceDelegation> => {
      const response = await fetchJson<{ success: boolean; delegation: InstanceDelegation }>(
        `/membership/resource-instances/${encodeURIComponent(instanceId)}/delegate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(request),
        }
      );
      return response.delegation;
    },

    /**
     * Check if a user has access to a resource instance
     */
    checkInstanceAccess: async (instanceId: string, identityId: string, action: string): Promise<boolean> => {
      const result = await fetchJson<{ has_access: boolean }>(
        `/membership/resource-instances/${encodeURIComponent(instanceId)}/check-access?identity_id=${encodeURIComponent(identityId)}&action=${encodeURIComponent(action)}`
      );
      return result.has_access;
    },

    // ========================================================================
    // Feature Bundles (v2.4)
    // ========================================================================

    /**
     * List available feature bundles for an application
     */
    listBundles: async (appId: string): Promise<FeatureBundle[]> => {
      const result = await fetchJson<{ items: FeatureBundle[] }>(
        `/membership/apps/${encodeURIComponent(appId)}/bundles`
      );
      return result.items || [];
    },

    /**
     * Get a feature bundle by ID
     */
    getBundle: async (bundleId: string): Promise<FeatureBundle> => {
      return fetchJson<FeatureBundle>(
        `/membership/bundles/${encodeURIComponent(bundleId)}`
      );
    },

    /**
     * Get resolved bundles with inherited permissions for an application
     */
    getResolvedBundles: async (appId: string): Promise<ResolvedBundle[]> => {
      const result = await fetchJson<{ items: ResolvedBundle[] }>(
        `/membership/apps/${encodeURIComponent(appId)}/bundles?resolved=true`
      );
      return result.items || [];
    },

    /**
     * Get the current bundle assignment for a user in an application
     */
    getUserBundleAssignment: async (appId: string, userId: string): Promise<BundleAssignment | null> => {
      try {
        return await fetchJson<BundleAssignment>(
          `/membership/apps/${encodeURIComponent(appId)}/users/${encodeURIComponent(userId)}/bundle-assignment`
        );
      } catch {
        return null;
      }
    },

    /**
     * Assign a bundle to a user (replaces any existing bundle assignment)
     */
    assignBundle: async (appId: string, userId: string, bundleId: string): Promise<BundleAssignment> => {
      return fetchJson<BundleAssignment>(
        `/membership/apps/${encodeURIComponent(appId)}/users/${encodeURIComponent(userId)}/bundle-assignment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ bundle_id: bundleId }),
        }
      );
    },

    /**
     * Get permission diff between two bundles
     */
    getBundleDiff: async (appId: string, fromBundleId: string, toBundleId: string): Promise<BundleDiff> => {
      return fetchJson<BundleDiff>(
        `/membership/apps/${encodeURIComponent(appId)}/bundles/diff?from=${encodeURIComponent(fromBundleId)}&to=${encodeURIComponent(toBundleId)}`
      );
    },

    /**
     * Get expanded permissions for a bundle (includes inherited)
     */
    getBundlePermissions: async (appId: string, bundleId: string): Promise<string[]> => {
      const result = await fetchJson<{ permissions: string[] }>(
        `/membership/apps/${encodeURIComponent(appId)}/bundles/${encodeURIComponent(bundleId)}/permissions`
      );
      return result.permissions || [];
    },

    // ========================================================================
    // Permission Sets (v2.4)
    // ========================================================================

    /**
     * List available permission sets for an application
     */
    listPermissionSets: async (appId: string): Promise<PermissionSet[]> => {
      const result = await fetchJson<{ items: PermissionSet[] }>(
        `/membership/permission-sets?app_id=${encodeURIComponent(appId)}`
      );
      return result.items || [];
    },

    /**
     * Get a user's effective permissions (bundle + additive permission sets)
     */
    getEffectivePermissions: async (userId: string): Promise<EffectivePermissions> => {
      return fetchJson<EffectivePermissions>(
        `/membership/identities/${encodeURIComponent(userId)}/effective-permissions`
      );
    },

    /**
     * Assign a permission set to a user (additive on top of bundle)
     */
    assignPermissionSet: async (userId: string, setId: string): Promise<void> => {
      await fetchJson<{ success: boolean }>(
        `/membership/identities/${encodeURIComponent(userId)}/permission-sets/${encodeURIComponent(setId)}`,
        { method: 'POST' }
      );
    },

    /**
     * Remove a permission set from a user
     */
    revokePermissionSet: async (userId: string, setId: string): Promise<void> => {
      await fetchJson<{ success: boolean }>(
        `/membership/identities/${encodeURIComponent(userId)}/permission-sets/${encodeURIComponent(setId)}`,
        { method: 'DELETE' }
      );
    },

    // ========================================================================
    // Utility Functions
    // ========================================================================

    /**
     * Parse ARN into components
     * @example parseArn('auth:account:empowernow:test') => { type: 'account', provider: 'empowernow', id: 'test' }
     */
    parseArn: (arn: string): { type: string; provider: string; id: string } | null => {
      const parts = arn.split(':');
      if (parts.length < 4 || parts[0] !== 'auth') return null;
      return {
        type: parts[1],
        provider: parts[2],
        id: parts.slice(3).join(':'),
      };
    },

    /**
     * Build canonical ARN
     * @example buildArn('account', 'empowernow', 'test') => 'auth:account:empowernow:test'
     */
    buildArn: (type: string, provider: string, id: string): string => {
      return `auth:${type}:${provider}:${id}`;
    },

    /**
     * Get display name from ARN (extracts last segment)
     */
    getDisplayNameFromArn: (arn: string): string => {
      return arn.split(':').pop() || arn;
    },

    /**
     * Determine identity type from ARN
     */
    getTypeFromArn: (arn: string): 'person' | 'group' | 'account' | 'agent' | 'service' | 'unknown' => {
      if (arn.includes(':account:')) return 'account';
      if (arn.includes(':agent:')) return 'agent';
      if (arn.includes(':service:')) return 'service';
      if (arn.includes(':group:')) return 'group';
      if (arn.includes(':person:')) return 'person';
      return 'unknown';
    },
  };
}

export type MembershipSdk = ReturnType<typeof createMembershipSdk>;

